import { useState } from "react";
import { CodeTokens } from "#/components/CodeTokens.tsx";
import { Icon } from "#/components/Icon.tsx";
import type { Messages } from "#/i18n/index.ts";

/** How long the button says "Copied" after a successful copy, in milliseconds. */
const COPIED_MS = 2000;

/**
 * A shell command with a copy button. The command is selectable text, so it
 * can still be copied by hand before the page's script loads.
 *
 * @param props - The command without the `$` prompt, an optional accessible
 *   label for the command, whether it is a text snippet (shown without the
 *   prompt, for example Markdown for a README), and the copy.
 * @returns The command line.
 */
export function CommandLine({ command, label, snippet = false, messages }: { command: string; label?: string; snippet?: boolean; messages: Messages }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className={snippet ? "command command--snippet" : "command"} role="group" aria-label={label ?? command}>
      <code translate="no">
        <CodeTokens text={command} />
      </code>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard.writeText(command).then(() => {
            setCopied(true);
            setTimeout(() => {
              setCopied(false);
            }, COPIED_MS);
          });
        }}
      >
        <Icon name={copied ? "check" : "copy"} size={16} />
        <span aria-live="polite">{copied ? messages.copy.copied : messages.copy.copy}</span>
      </button>
    </div>
  );
}
